import {
  booleanAttribute,
  Component,
  computed,
  ElementRef,
  forwardRef,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { afterRenderEffect } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { MibLabelPosition, MibPalette } from '../../foundations';
import { MIB_PALETTE_DOT } from '../badge';
import { MibSwitchSize, SWITCH_SIZE } from './switch.models';

/**
 * An option turned on or off, drawn as its projected label and a switch beside it.
 *
 * The control is only itself: it takes the width of what it holds, carries no ground of its own and
 * no height beyond its parts, so the row, the card and the line between two of them belong to the
 * page. Widen the host and tell it how to justify, and the label and the switch part company —
 * `justify-content` is inherited by the button, which is the only way a class written on the tag
 * can reach inside it.
 *
 * Label and switch together are one `<button>`, which is what gives the space bar, the Enter key
 * and the focus ring without a key handler. There is no native switch, so `role="switch"` and its
 * checked state are what tell a screen reader what it is reading.
 */
@Component({
  selector: 'mib-switch',
  templateUrl: './switch.component.html',
  host: { class: 'inline-flex' },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MibSwitchComponent),
      multi: true,
    },
  ],
})
export class MibSwitchComponent implements ControlValueAccessor {
  /** Whether the option is on. Two-way, and written by a reactive form when there is one. */
  readonly checked = model(false);
  /** Neither on nor off, as a group partly granted. Pressed, it turns on and clears itself. */
  readonly mixed = model(false);
  /** Which side of the switch the projected label sits on. */
  readonly position = input<MibLabelPosition>('left');
  /** The step it is drawn at: a 36×20, 44×24 or 52×28px track. */
  readonly size = input<MibSwitchSize>('md');
  /** The ramp the track is filled with once on. Left out, it takes the action colour. */
  readonly palette = input<MibPalette | null>(null);
  /** Names the switch when nothing is projected into it. Ignored when something is. */
  readonly ariaLabel = input<string | null>(null, { alias: 'aria-label' });
  /** Refuses to flip, and says so. */
  readonly disabled = input(false, { transform: booleanAttribute });
  /** Asks instead of flipping: a press only emits `checkedRequest`, and the switch shows what `[checked]` says. */
  readonly controlled = input(false, { transform: booleanAttribute });
  /** Emits the state asked for when `controlled` is set. */
  readonly checkedRequest = output<boolean>();

  /* A reactive form disables through `setDisabledState`, which is a call and not an input. */
  private readonly formDisabled = signal(false);
  protected readonly isDisabled = computed(() => this.disabled() || this.formDisabled());

  private readonly step = computed(() => SWITCH_SIZE[this.size()]);

  /** The ramp's fill step, or the action colour when no ramp was named. */
  private readonly fill = computed(() => {
    const palette = this.palette();
    return palette ? MIB_PALETTE_DOT[palette] : 'bg-mib-primary';
  });

  private readonly labelBox = viewChild.required<ElementRef<HTMLElement>>('labelBox');

  /** Whether anything was projected, which decides if {@link ariaLabel} is the name or is ignored. */
  protected readonly hasLabel = signal(false);

  constructor() {
    afterRenderEffect(() => this.hasLabel.set(this.labelBox().nativeElement.childNodes.length > 0));
  }

  protected readonly controlClasses = computed(
    () =>
      'flex w-full items-center rounded-mib text-left select-none outline-mib-primary focus-visible:outline-2 focus-visible:outline-offset-2 ' +
      this.step().gap +
      (this.position() === 'right' ? ' flex-row-reverse' : '') +
      (this.isDisabled() ? ' cursor-not-allowed opacity-60' : ' cursor-pointer'),
  );

  protected readonly labelClasses = computed(
    () =>
      this.step().text + ' min-w-0 truncate empty:hidden ' + (this.isDisabled() ? 'text-gray-400' : 'text-gray-900'),
  );

  protected readonly trackClasses = computed(
    () =>
      'flex shrink-0 items-center rounded-full p-0.5 transition-colors motion-reduce:transition-none ' +
      this.step().track +
      (this.checked() || this.mixed() ? ' ' + this.fill() : ' bg-gray-300'),
  );

  protected readonly knobClasses = computed(
    () =>
      'rounded-full bg-white shadow-sm transition-transform motion-reduce:transition-none ' +
      this.step().knob +
      ' ' +
      (this.mixed() ? this.step().middle : this.checked() ? this.step().travel : 'translate-x-0'),
  );

  protected readonly ariaChecked = computed(() => (this.mixed() ? 'mixed' : String(this.checked())));

  protected toggle(): void {
    if (this.isDisabled()) {
      return;
    }
    const next = this.mixed() || !this.checked();
    if (this.controlled()) {
      this.checkedRequest.emit(next);
      return;
    }
    this.mixed.set(false);
    this.checked.set(next);
    this.onChange(next);
  }

  private onChange: (value: boolean) => void = () => undefined;
  protected onTouched: () => void = () => undefined;

  /** Called by a form to write its value into the control. */
  writeValue(value: boolean | null): void {
    this.checked.set(!!value);
  }

  /** Called by a form to be told when the user changes the value. */
  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  /** Called by a form to be told when the user leaves the control. */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /** Called by a form to switch the control off or back on. */
  setDisabledState(isDisabled: boolean): void {
    this.formDisabled.set(isDisabled);
  }
}
